// One MediaPipe PoseLandmarker for the whole tab.
//
// Each landmarker carries its own WASM heap and WebGL context (~110 MB in
// Chromium) and MediaPipe never frees them unless close() is called. The scan
// used to create one per pose, so a fourth four-view scan in the same tab made
// the 16th: past the browser's WebGL context limit, and more memory than a
// phone gives a tab. The detector then failed to load on the flex pose.
// Every pose and every scan now shares this one.

import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";

let pending: Promise<PoseLandmarker> | null = null;

async function create(): Promise<PoseLandmarker> {
  const vision = await FilesetResolver.forVisionTasks("/shared/wasm");
  const options = (delegate: "GPU" | "CPU") => ({
    baseOptions: {
      modelAssetPath: `/shared/models/pose_landmarker_lite.task`,
      delegate,
    },
    runningMode: "VIDEO" as const,
    minPosePresenceConfidence: 0.5,
    numPoses: 1,
    minTrackingConfidence: 0.1,
  });
  try {
    const landmarker = await PoseLandmarker.createFromOptions(vision, options("GPU"));
    console.log("MediaPipe: GPU delegate loaded");
    return landmarker;
  } catch (gpuErr) {
    console.warn("MediaPipe: GPU delegate failed, falling back to CPU", gpuErr);
    const landmarker = await PoseLandmarker.createFromOptions(vision, options("CPU"));
    console.log("MediaPipe: CPU delegate loaded");
    return landmarker;
  }
}

export function getPoseLandmarker(): Promise<PoseLandmarker> {
  if (!pending) {
    pending = create().catch((err) => {
      // Let the next caller try again rather than caching the failure.
      pending = null;
      throw err;
    });
  }
  return pending;
}
